import Vue from 'vue';
import App from './App.vue';
import router from './router/app.router';
import vuetify from './plugins/vuetify';
import VCalendar from 'v-calendar';

// Use plugin with optional defaults
// import axios from 'axios';
import { axiosInstance, axiosNewAPiInstance } from '@/services/base';
import { logout, refreshToken, updateAuthTokens } from '@/services/auth.services';

import './plugins/vue-wysiwyg';
import './plugins/view-design';
import './assets/styles/style.scss';

import '@/mixins/base';

import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';

/*global process*/
/*eslint no-undef: "error"*/

Bugsnag.start({
    apiKey: 'adb9badeaddfbe77fb860ca6796e3d3c',
    releaseStage: process.env.VUE_APP_ENV,
    plugins: [new BugsnagPluginVue()],
});

const bugsnagVue = Bugsnag.getPlugin('vue');
bugsnagVue.installVueErrorHandler(Vue);

Vue.config.productionTip = false;
Vue.prototype.$http = axiosInstance;
// axiosInstance.defaults.timeout = 10000;

router.beforeEach((to, from, next) => {
    if (!to.matched.length) {
        next('/404-page-not-found');
    } else {
        const userRole = window.localStorage.getItem('mainRole');
        if (to.meta.requiredAuth) {
            if (window.localStorage.getItem('is_authenticated')) {
                if (to.meta.roles.includes(userRole)) {
                    next();
                } else {
                    if (userRole === 'dev' || userRole === 'pm') {
                        router.push({ name: 'UserDashboard' }).catch(() => {});
                    }
                    if (userRole === 'admin') {
                        router.push({ name: 'Projects' }).catch(() => {});
                    }
                    if (userRole === 'client') {
                        router.push({ name: 'Client' }).catch(() => {});
                    }
                }
            } else {
                next('/login');
            }
        } else {
            next();
        }
    }
});

axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.common['Authorization'] = token;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

let isRefreshingToken = false;
let pendingRequests = [];

function resumePendingRequests(newToken) {
    pendingRequests.forEach((callback) => callback(newToken));
    pendingRequests = [];
}

function forceLogout() {
    try {
        logout();
    } finally {
        window.location.href = '/login';
    }
}

function handleUnauthorized(instance, error) {
    const originalRequest = error.config;
    const storedRefreshToken = localStorage.getItem('refresh_token');

    if (
        !storedRefreshToken ||
        !originalRequest ||
        originalRequest.url.includes('/auth/refresh') ||
        originalRequest._retry
    ) {
        forceLogout();
        return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (!isRefreshingToken) {
        isRefreshingToken = true;
        refreshToken()
            .then((res) => {
                updateAuthTokens({
                    access_token: res.data.token,
                    refresh_token: res.data.refreshToken,
                });
                isRefreshingToken = false;
                resumePendingRequests(res.data.token);
            })
            .catch((e) => {
                Bugsnag.notify(e);
                isRefreshingToken = false;
                resumePendingRequests(null);
                forceLogout();
            });
    }

    return new Promise((resolve, reject) => {
        pendingRequests.push((newToken) => {
            if (!newToken) {
                reject(error);
                return;
            }
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(instance(originalRequest));
        });
    });
}

function attachAuthInterceptor(instance, onFulfilled) {
    instance.interceptors.response.use(onFulfilled, (error) => {
        if (error.response && error.response.status === 401) {
            return handleUnauthorized(instance, error);
        }
        return Promise.reject(error);
    });
}

attachAuthInterceptor(axiosInstance, (response) => {
    if (response.status === 200 || response.status === 201) {
        return Promise.resolve(response);
    }
    return Promise.reject(response);
});
attachAuthInterceptor(axiosNewAPiInstance, (response) => response);

Vue.use(VCalendar, {
    componentPrefix: 'vc',
});
new Vue({
    vuetify,
    router,
    render: (h) => h(App),
}).$mount('#app');
